import moment from 'moment';
import i18next from 'i18next';
import {
    CYCLE_TYPE_NONE,
    CYCLE_TYPE_REGULAR,
    CYCLE_TYPE_IRREGULAR,
    CYCLE_TYPE_PREGNANT,
    DATE_FORMAT,
    HORMONAL_CONTRACEPTION_NONE,
    CYCLE_TYPE_INACTIVE,
    CONTRACEPTION_TYPE_CONTRACEPTIVE_INJECTIONS,
    CONTRACEPTION_TYPE_CONTRACEPTIVE_PATCH,
    CONTRACEPTION_TYPE_IUD,
    CONTRACEPTION_TYPE_IMPLANT,
    CONTRACEPTION_TYPE_ORAL_CONTRACEPTION,
    CONTRACEPTION_TYPE_VAGINAL_RING,
    CONTRACEPTION_TYPE_NONE
} from '../constants/MenstrualCycleTrackingConstants';
import { getFeatureEnabledForMultipleRoles, isMale } from './UserUtil';
import AnalyticsUtil from 'utils/AnalyticsUtil';
import {
    Color106 as chartWhite,
    Color142 as chartGray,
    Color419 as chartGreen,
    Color704 as chartBerry,
    ColorDarkuiAccent3,
    ColorGreenPrimary,
    ColorVioletPrimary
} from 'ui-connect/styles/default-theme';

export const showMenstrualCycleTracking = (mctFeatureEnabled, userSettings, menstrualCycleTrackingFeatureData) => {
    return (
        mctFeatureEnabled &&
        (!isMale(userSettings) || isMenstrualCycleConfigured(userSettings)) &&
        !!menstrualCycleTrackingFeatureData
    );
};

//This is different from showMenstrualCycleTracking as in it does not check for menstrualCycleTrackingFeatureData - so the MCT is not necessarily configured
export const showMenstrualCycleTrackingWithoutMCTSettingsData = (mctFeatureEnabled, userSettings) => {
    return mctFeatureEnabled && (!isMale(userSettings) || isMenstrualCycleConfigured(userSettings));
};

export const isUserInactive = (userSettings, userIsInactiveStatus) => {
    // it seems that the menstrualCycleType === NONE user can never be inactive
    return (
        userSettings &&
        userSettings.userMenstrualCycleSettings &&
        userSettings.userMenstrualCycleSettings.menstrualCycleType !== CYCLE_TYPE_NONE &&
        userIsInactiveStatus
    );
};

export const isUserMenstrualCycleTypeInactive = (userSettings) => {
    return (
        userSettings &&
        userSettings.userMenstrualCycleSettings &&
        userSettings.userMenstrualCycleSettings.menstrualCycleType === CYCLE_TYPE_INACTIVE
    );
};

export const isMenstrualCycleConfigured = (userSettings) => {
    return (
        userSettings &&
        userSettings.userMenstrualCycleSettings &&
        (userSettings.userMenstrualCycleSettings.menstrualCycleType !== 'REGULAR' ||
            userSettings.userMenstrualCycleSettings.avgCycleLength > 1)
    );
};

/*returns true if this is a past cycle*/
export const isPastCycle = (educationContentMod) => {
    return isNaN(educationContentMod);
};

/*returns true if 3 days has past since the cycle ended*/
export const isThreeDaysAfterCycle = (daySummary) => {
    return daySummary.dayInCycle === daySummary.periodLength + 4;
};

export const isPredictedCycle = (daySummary) => {
    /*returns true if the cycle was predicted or entered by the user*/
    return daySummary.predictedCycle;
};

//is a prediction selected in userSettings
export const isAPredictionSelected = (userMenstrualCycleSettings) => {
    return (
        userMenstrualCycleSettings.periodPrediction ||
        (userMenstrualCycleSettings.ovulationPrediction && userMenstrualCycleSettings.periodPrediction)
    );
};

/*this does not includes ovulation tracking and periodPrediction and flow*/
export const hasSelectedAnyMCTLoggingCategoryExcludingFlow = (userMenstrualCycleSettings) => {
    return (
        userMenstrualCycleSettings &&
        (userMenstrualCycleSettings.dischargeTracking ||
            (userMenstrualCycleSettings.physicalSymptomsTracking &&
                userMenstrualCycleSettings.menstrualCycleType !== CYCLE_TYPE_NONE) ||
            userMenstrualCycleSettings.moodTracking ||
            userMenstrualCycleSettings.ovulationDateTracking ||
            userMenstrualCycleSettings.physicalSymptomsTracking ||
            userMenstrualCycleSettings.sexDriveTracking ||
            userMenstrualCycleSettings.sexualActivityTracking)
    );
};

export const isCycleTypeNoneSet = (userMenstrualCycleSettings) => {
    return userMenstrualCycleSettings && userMenstrualCycleSettings.menstrualCycleType === CYCLE_TYPE_NONE;
};

//returns true if flow is selected in userSettings
export const hasSelectedMCTLoggingCategoryFlow = (userMenstrualCycleSettings) => {
    return userMenstrualCycleSettings && userMenstrualCycleSettings.flowTracking;
};

//returns true if any logging category is selected by the user in user settings
export const hasSelectedAnyMCTLoggingCategory = (userMenstrualCycleSettings) => {
    return (
        hasSelectedAnyMCTLoggingCategoryExcludingFlow(userMenstrualCycleSettings) ||
        hasSelectedMCTLoggingCategoryFlow(userMenstrualCycleSettings)
    );
};

/*this includes also ovulation tracking and periodPrediction and flow*/
export const isAPredictionOrTrackingOn = (userMenstrualCycleSettings) => {
    return (
        isAPredictionSelected(userMenstrualCycleSettings) ||
        hasSelectedAnyMCTLoggingCategoryExcludingFlow(userMenstrualCycleSettings) ||
        hasSelectedMCTLoggingCategoryFlow(userMenstrualCycleSettings)
    );
};

export const createPostPayload = (dailyLogData, profileId, date, reportTimestamp) => {
    let payload = {
        ...dailyLogData,
        userProfilePk: profileId,
        calendarDate: date,
        reportTimestamp: reportTimestamp
    };
    /*remove keys with no values*/
    for (let propName in payload) {
        //the notes symptom is an exception so if the user deletes its we must send empty string or the backend will not delete the note
        if (
            payload[propName] === null ||
            payload[propName] === undefined ||
            (payload[propName].length === 0 && propName !== 'notes')
        ) {
            delete payload[propName];
        }
    }

    return payload;
};

export const isPeriodDayLogged = (daySummary) => {
    return daySummary.predictedCycle === false && daySummary.currentPhase === 1;
};

export const noPeriodDayLogged = (daySummary) => {
    return daySummary.predictedCycle === true || daySummary.currentPhase === 1;
};

export const setMenstrualCycleAndFertileDates = (
    menstrualCycleTrackingCalendar,
    singleAddedMenstrualCalendar,
    alreadySetSelectedDates,
    removedDates = []
) => {
    let selectedDates = {
        loggedOvulationDays: [],
        loggedSymptomDays: [],
        fertileDates: [],
        menstrualDates: [],
        loggedNoteDays: [],
        overlapDates: []
    };

    let menstrualCycleTrackingCalendarValues = singleAddedMenstrualCalendar
        ? singleAddedMenstrualCalendar
        : menstrualCycleTrackingCalendar;

    Object.keys(menstrualCycleTrackingCalendarValues).forEach((calendarNumberKey) => {
        let currentCalendar = menstrualCycleTrackingCalendarValues[calendarNumberKey];

        Object.keys(currentCalendar.loggedSymptomDays).forEach((symptomDays) => {
            let currentSymptomDay = currentCalendar.loggedSymptomDays[symptomDays];
            if (selectedDates.loggedSymptomDays.indexOf(currentSymptomDay) === -1) {
                selectedDates.loggedSymptomDays.push(currentSymptomDay);
            }
        });

        Object.keys(currentCalendar.loggedOvulationDays).forEach((ovulationDays) => {
            let currentOvulationDay = currentCalendar.loggedOvulationDays[ovulationDays];
            if (selectedDates.loggedOvulationDays.indexOf(currentOvulationDay) === -1) {
                selectedDates.loggedOvulationDays.push(currentOvulationDay);
            }
        });

        Object.keys(currentCalendar.loggedNoteDays).forEach((noteDays) => {
            let currentNoteDay = currentCalendar.loggedNoteDays[noteDays];
            if (selectedDates.loggedNoteDays.indexOf(currentNoteDay) === -1) {
                selectedDates.loggedNoteDays.push(currentNoteDay);
            }
        });

        Object.keys(currentCalendar.cycleSummaries).forEach((cycleSummary) => {
            let currentCycleSummary = currentCalendar.cycleSummaries[cycleSummary];

            for (
                let menstrualDateCount = 0;
                menstrualDateCount < currentCycleSummary.periodLength;
                menstrualDateCount++
            ) {
                let momentDate = moment(currentCycleSummary.startDate)
                    .add(menstrualDateCount, 'days')
                    .format(DATE_FORMAT);
                if (
                    selectedDates.menstrualDates.indexOf(momentDate) === -1 &&
                    removedDates.indexOf(momentDate) === -1
                ) {
                    selectedDates.menstrualDates.push(momentDate);
                }
            }

            for (
                let fertileWindowCount = 0;
                fertileWindowCount < getFertileWindowLength(currentCycleSummary);
                fertileWindowCount++
            ) {
                //days added is fertileWindowCount-1 in this case because it need to take into account also the first day from which it starts
                let fertileWindowDateValue = getFertileWindowStart(currentCycleSummary) + fertileWindowCount - 1;
                let fertileWindowDates = fertileWindowDateValue > 0 ? fertileWindowDateValue : 0;
                let momentDate = moment(currentCycleSummary.startDate)
                    .add(fertileWindowDates, 'days')
                    .format(DATE_FORMAT);
                if (selectedDates.fertileDates.indexOf(momentDate) === -1 && removedDates.indexOf(momentDate) === -1) {
                    selectedDates.fertileDates.push(momentDate);
                }
            }

            if (currentCycleSummary.overlapCycleList) {
                for (
                    let overlapDateCount = 0;
                    overlapDateCount < currentCycleSummary.overlapCycleList[0].periodLength;
                    overlapDateCount++
                ) {
                    let momentDate = moment(currentCycleSummary.overlapCycleList[0].startDate)
                        .add(overlapDateCount, 'days')
                        .format(DATE_FORMAT);
                    if (selectedDates.overlapDates.indexOf(momentDate) === -1) {
                        selectedDates.overlapDates.push(momentDate);
                    }
                    if (selectedDates.menstrualDates.indexOf(momentDate) === -1) {
                        selectedDates.menstrualDates.push(momentDate);
                    }
                }
            }
        });
    });

    if (alreadySetSelectedDates) {
        Object.keys(alreadySetSelectedDates).forEach((datesArray) => {
            selectedDates[datesArray] = [...selectedDates[datesArray], ...alreadySetSelectedDates[datesArray]].filter(
                (item, pos) =>
                    [...selectedDates[datesArray], ...alreadySetSelectedDates[datesArray]].indexOf(item) === pos
            );
        });
    }

    return selectedDates;
};

export const getStartOfMonth = (momentDate) => momentDate.startOf('month').format(DATE_FORMAT);

export const getEndOfMonth = (momentDate) => momentDate.endOf('month').format(DATE_FORMAT);

export const getMenstrualCycleStartDate = (menstrualCycleKey) => menstrualCycleKey.split('/')[0];

export const getAllSequentialDatesFromArray = (diffDates, allDates) => {
    const getDate = (i, date) => {
        return moment(diffDates[i])
            .add(date, 'd')
            .format(DATE_FORMAT);
    };

    //if added date is next to a cycle set the entire cycle as added
    for (let i in diffDates) {
        let nextDates = 1;
        let prevDates = -1;
        //check for future dates
        while (allDates.indexOf(getDate(i, nextDates)) !== -1 && diffDates.indexOf(getDate(i, nextDates)) === -1) {
            diffDates.push(getDate(i, nextDates));
            nextDates++;
        }
        //check for past dates
        while (allDates.indexOf(getDate(i, prevDates)) !== -1 && diffDates.indexOf(getDate(i, prevDates)) === -1) {
            diffDates.push(getDate(i, prevDates));
            prevDates--;
        }
    }

    return diffDates;
};

export const setOrUnsetCurrentPeriodEditingInFutureDates = (menstrualDates, currentSelectedDate, removeDate) => {
    let currentCycleThatContinuesInTheFuture = [];
    let todayDate = moment().format(DATE_FORMAT);
    let allCalendarMenstrualDatesIncludingTheAddedDate = [...menstrualDates];

    if (!removeDate && allCalendarMenstrualDatesIncludingTheAddedDate.indexOf(currentSelectedDate) === -1) {
        allCalendarMenstrualDatesIncludingTheAddedDate.push(currentSelectedDate);
    } else if (removeDate && allCalendarMenstrualDatesIncludingTheAddedDate.indexOf(currentSelectedDate) !== -1) {
        let dateIndex = allCalendarMenstrualDatesIncludingTheAddedDate.indexOf(currentSelectedDate);
        allCalendarMenstrualDatesIncludingTheAddedDate.splice(dateIndex, 1);
    }

    if (
        allCalendarMenstrualDatesIncludingTheAddedDate.length &&
        allCalendarMenstrualDatesIncludingTheAddedDate.indexOf(todayDate) !== -1
    ) {
        currentCycleThatContinuesInTheFuture = getAllSequentialDatesFromArray(
            [todayDate],
            allCalendarMenstrualDatesIncludingTheAddedDate
        );
    }

    return [...currentCycleThatContinuesInTheFuture];
};

export const getMenstrualCycleCalendarChangedDates = (removedDates, initialDates) => {
    let currentDate = moment(new Date()).format(DATE_FORMAT);

    let removedDifferentDatesInArray = initialDates.filter((date) => removedDates.indexOf(date) < 0).sort();
    let addedDifferentDatesInArray = removedDates.filter((date) => initialDates.indexOf(date) < 0).sort();
    let changes = removedDifferentDatesInArray.length > 0 || addedDifferentDatesInArray.length > 0;
    let datesObject = {};
    let arrayOfCycles = [];
    let currentCycle = [];
    let currentCycleDate = null;

    if (changes) {
        //doing this because if a date is added next to a cycle we need to add the entire cycle - or the api will take it as a separate cycle next to the already added cycle
        addedDifferentDatesInArray = getAllSequentialDatesFromArray(addedDifferentDatesInArray, initialDates);
        removedDifferentDatesInArray = getAllSequentialDatesFromArray(removedDifferentDatesInArray, initialDates);

        let lastDayChangedWithCycle = [...addedDifferentDatesInArray, ...removedDifferentDatesInArray].sort();

        if (
            lastDayChangedWithCycle &&
            lastDayChangedWithCycle.length &&
            lastDayChangedWithCycle[lastDayChangedWithCycle.length - 1] > currentDate
        ) {
            //if the sequential cycle day is after today - set that day as the end date
            currentDate = lastDayChangedWithCycle[lastDayChangedWithCycle.length - 1];
        }

        let allRemainingMenstrualDates = removedDates.filter((item) => item <= currentDate);
        let allInitialMenstrualDates = initialDates.filter((item) => item <= currentDate);
        //used just to get the start date and end date - do not create arrays of cycles after this
        let allChangedDatesInArray = [...allInitialMenstrualDates, ...allRemainingMenstrualDates].sort();
        let startDate = allChangedDatesInArray[0];
        let endDate = allChangedDatesInArray[allChangedDatesInArray.length - 1];
        datesObject = {
            startDate,
            endDate
        };

        allRemainingMenstrualDates.sort().map(function(item, index) {
            if (item >= startDate && item <= endDate) {
                if (currentCycleDate !== null && moment(item) > moment(currentCycleDate).add(1, 'day')) {
                    arrayOfCycles.push(currentCycle);
                    currentCycle = [];
                }
                currentCycle.push(item);
                currentCycleDate = item;

                if (endDate === item) {
                    arrayOfCycles.push(currentCycle);
                    currentCycle = [];
                }
            }
        });

        // if last item was removed from allRemainingMenstrualDates make sure to add the last created cycle period
        if (currentCycle.length > 0) {
            arrayOfCycles.push(currentCycle);
            currentCycle = [];
        }
    }

    datesObject.arrayOfCycles = [...arrayOfCycles];

    return datesObject;
};

export const getFertileWindowStart = (daySummary) => {
    const { fertileWindowStart } = daySummary;
    if (fertileWindowStart < 0) {
        return 0;
    } else {
        return fertileWindowStart;
    }
};

export const getFertileWindowLength = (daySummary) => {
    const { fertileWindowStart, lengthOfFertileWindow } = daySummary;
    if (fertileWindowStart < 0) {
        return Math.max(lengthOfFertileWindow + fertileWindowStart, 0);
    } else {
        return lengthOfFertileWindow;
    }
};

export const getChartDetails = (daySummary, date, userIsInactive, userSettings) => {
    return {
        selectedDate: date,
        isAPredictionSelected:
            !!(daySummary && daySummary.periodLength) || (daySummary && daySummary.fertileWindowStart),
        userIsInactive: userIsInactive,
        customWidth: 227,
        customHeight: 227,
        dayInCycle: daySummary ? daySummary.dayInCycle : null,
        currentPhase: daySummary ? daySummary.currentPhase : null,
        daysUntilNextPhase: daySummary ? daySummary.daysUntilNextPhase : null,
        predictedCycleLength: getPredictiedCycleLength(daySummary, userSettings),
        predictedCycle: daySummary ? daySummary.predictedCycle : null,
        lengthOfFertileWindow: daySummary ? getFertileWindowLength(daySummary) : null,
        fertileWindowStart: daySummary ? getFertileWindowStart(daySummary) : null,
        periodLength: daySummary ? daySummary.periodLength : null,
        lutealPhaseStart: daySummary ? daySummary.lutealPhaseStart : null,
        isMenstrualCycleConfigured: isMenstrualCycleConfigured(userSettings),
        isCycleTypeNone:
            userSettings &&
            userSettings.userMenstrualCycleSettings &&
            userSettings.userMenstrualCycleSettings.menstrualCycleType === CYCLE_TYPE_NONE,
        periodPrediction: daySummary && daySummary.periodLength ? true : false,
        contraception: getContraception(daySummary),
        ovulationPrediction: daySummary && exists(daySummary.fertileWindowStart) ? true : false
    };
};

const getPredictiedCycleLength = (daySummary, userSettings) => {
    if (daySummary && daySummary.predictedCycleLength) {
        return daySummary.predictedCycleLength;
    } else if (userSettings && userSettings.userMenstrualCycleSettings) {
        return userSettings.userMenstrualCycleSettings.avgPeriodLength;
    } else {
        return 0;
    }
};

const getContraception = (daySummary) => {
    let contraception = 'NONE';

    if (daySummary && daySummary.periodLength) {
        if (exists(daySummary.fertileWindowStart) && !daySummary.lutealPhaseStart) {
            contraception = 'NONE';
        } else if (!exists(daySummary.fertileWindowStart) && daySummary.lutealPhaseStart) {
            contraception = 'NONE';
        } else {
            contraception = 'OTHER';
        }
    }

    return contraception;
};

const exists = (val) => {
    return typeof val !== 'undefined' && val !== null;
};

export const showPhasesLabels = (userInactive, currentPhase, mctUserSettings) => {
    //cyle type none
    //all predictions off
    //ovulation prediction off
    //inactive for 6 cycles
    //no initial setup
    return (
        (userInactive ||
            (mctUserSettings &&
                (mctUserSettings.periodPrediction || mctUserSettings.ovulationPrediction) &&
                mctUserSettings.contraception === HORMONAL_CONTRACEPTION_NONE)) &&
        mctUserSettings.periodPrediction &&
        !userInactive &&
        currentPhase !== ''
    );
};

export const getCurrentPhaseString = (ovulationPrediction, currentPhase, noContraception) => {
    switch (currentPhase) {
        case 1:
            return 'label_phase_name_menstrual';
        case 2:
            return 'label_phase_name_follicular';
        case 3:
            return noContraception
                ? ovulationPrediction
                    ? 'label_phase_name_ovulation'
                    : 'label_phase_name_follicular'
                : 'label_phase_name_luteal';
        case 4:
            return 'label_phase_name_luteal';
        default:
            return '--';
    }
};

export const getNextLabelString = (currentPhase, ovulationPrediction, noContraception) => {
    if (currentPhase === 1) {
        return 'label_next_phase';
    } else if (currentPhase === 2) {
        //ovulation prediction off and using contraception
        if (!noContraception && ovulationPrediction) {
            return 'label_next_period';
        }
        return 'label_next_phase';
    } else if (currentPhase === 3) {
        //ovulation prediction off and not using contraception || ovulation prediction off and using contraception
        if ((noContraception && !ovulationPrediction) || (!noContraception && ovulationPrediction)) {
            return 'label_next_period';
        }
        return 'label_next_phase';
    } else {
        //phase 4
        return 'label_next_period';
    }
};

export const dateIsToday = (date) => {
    return moment(date).isSame(moment(), 'd');
};

export const moods = {
    FINE: [],
    HAPPY: [],
    ENERGETIC: [],
    MOTIVATED: [],
    MOOD_SWINGS: [],
    SAD: [],
    EMOTIONAL: [],
    OVERWHELMED: [],
    IRRITABLE: [],
    FRUSTRATED: [],
    WORRIED: []
};

export const discharge = {
    NO_DISCHARGE: [],
    CREAMY: [],
    EGG_WHITE: [],
    SPOTTING: [],
    STICKY: [],
    UNUSUAL: [],
    LIGHT: [],
    MEDIUM: [],
    HEAVY: []
};

export const symptomSort = (array, key) => {
    let keys = null;
    if (key === 'discharge') {
        keys = Object.keys(discharge);
    } else if (key === 'moods') {
        keys = Object.keys(moods);
    } else if (key === 'symptoms') {
        return sortPhysicalSymptoms(array);
    }

    if (keys !== null) {
        let copy = Array(keys.length);

        keys.forEach((key, index) => {
            array.forEach((item) => {
                if (item === key) {
                    copy.splice(index, 0, item);
                }
            });
        });

        return copy.filter((v) => {
            return typeof v !== 'undefined';
        });
    } else {
        return array;
    }
};

export const createSymptomsReportsObject = (data) => {
    let acceptableSymptoms = ['discharge', 'flow', 'moods', 'ovulationDay', 'sexDrive', 'sexualActivity', 'symptoms'];
    //must be set in a specific order - that is why the object is created - the order from the api is not ok
    let returnObj = {
        flow: {
            LIGHT: [],
            MEDIUM: [],
            HEAVY: []
        },
        symptoms: {
            ACNE: [],
            BACKACHE: [],
            BLOATING: [],
            BODY_ACHES: [],
            CONSTIPATION: [],
            CONTRACTIONS: [],
            CRAMPS: [],
            CRAVINGS: [],
            DIARRHEA: [],
            DIZZINESS: [],
            FATIGUE: [],
            HEADACHE: [],
            INSOMNIA: [],
            NAUSEA: [],
            TENDER_BREASTS: [],
            TROUBLE_BREATHING: [],
            CHILLS: [],
            DISTRACTED: [],
            DRY_SKIN: [],
            FREQUENT_URINATION: [],
            HAIR_LOSS: [],
            HOT_FLASHES: [],
            NIGHT_SWEATS: [],
            URINE_LEAKAGE: [],
            VAGINAL_DRYNESS: [],
            VOMITING: [],
            WEIGHT_GAIN: [],
            SWELLING: [],
            HEARTBURN: [],
            ROUND_LIGAMENT_PAIN: []
        },
        moods: Object.assign({}, moods),
        discharge: Object.assign({}, discharge),
        ovulationDay: {},
        sexualActivity: {
            PROTECTED: [],
            UNPROTECTED: []
        },
        sexDrive: { LOW: [], AVERAGE: [], HIGH: [] }
    };
    if (data) {
        data.forEach((item) => {
            let currentDataObject = item;
            if (currentDataObject.dailyLogs) {
                currentDataObject.dailyLogs.map(function(symptom) {
                    for (let j in symptom) {
                        if (symptom.hasOwnProperty(j) && acceptableSymptoms.indexOf(j) > -1 && symptom[j]) {
                            let addNewDateToSymptom = {};
                            if (typeof symptom[j] === 'object') {
                                addNewDateToSymptom = {
                                    ...symptom[j].reduce(function(map, obj) {
                                        map[obj] =
                                            returnObj[j] && returnObj[j][obj]
                                                ? [...returnObj[j][obj], symptom.calendarDate]
                                                : [symptom.calendarDate];
                                        return map;
                                    }, {})
                                };
                            } else if (typeof symptom[j] === 'string' || typeof symptom[j] === 'boolean') {
                                let val = symptom[j] === true ? j + '_pural' : symptom[j];
                                addNewDateToSymptom = { ...addNewDateToSymptom };
                                addNewDateToSymptom[val] =
                                    returnObj[j] && returnObj[j][val]
                                        ? [...returnObj[j][val], symptom.calendarDate]
                                        : [symptom.calendarDate];
                            }
                            returnObj[j] = {
                                ...returnObj[j],
                                ...addNewDateToSymptom
                            };
                        }
                    }
                });
            }
        });
    }
    //remove empty arrays from objects the remove empty objects
    for (let k in returnObj) {
        for (let n in returnObj[k]) {
            if (returnObj[k][n].length === 0) {
                delete returnObj[k][n];
            }
        }
        if (returnObj.hasOwnProperty(k) && Object.keys(returnObj[k]).length === 0) {
            delete returnObj[k];
        }
    }

    return returnObj;
};

export const getReportCyclesLength = (data) => {
    let cycleLength = 0;

    if (data) {
        Object.keys(data).forEach((objKey) => {
            let currentDataObject = data[objKey];
            if (currentDataObject.cycleSummaries) {
                Object.keys(currentDataObject.cycleSummaries).forEach((cycleSummaryKey) => {
                    let currentCycleSummary = currentDataObject.cycleSummaries[cycleSummaryKey];
                    cycleLength += currentCycleSummary.cycleLength;
                });
            } else {
                cycleLength += currentDataObject.avgCycleLength;
            }
        });
        return cycleLength;
    }
};

export const isAnyDataLoggedInDailyLogObject = (dailyLogData) => {
    //checking here for each log because we also get empty keys in the response
    return (
        dailyLogData &&
        ((dailyLogData.discharge && dailyLogData.discharge.length) ||
            (dailyLogData.flow && dailyLogData.flow.length) ||
            (dailyLogData.moods && dailyLogData.moods.length) ||
            dailyLogData.ovulationDay ||
            (dailyLogData.sexDrive && dailyLogData.sexDrive.length) ||
            (dailyLogData.sexualActivity && dailyLogData.sexualActivity.length) ||
            (dailyLogData.symptoms && dailyLogData.symptoms.length))
    );
};

export const isMctFeatureEnabled = (viewerSocialProfile) => {
    return getFeatureEnabledForMultipleRoles({ value: 'ROLE_WELLNESS_USER, ROLE_FITNESS_USER' }, viewerSocialProfile);
};

export const cycleTypes = [
    { value: CYCLE_TYPE_INACTIVE, label_translation: '--' },
    { value: CYCLE_TYPE_NONE, label_translation: 'no_period' },
    { value: CYCLE_TYPE_REGULAR, label_translation: 'regular_cycle_type' },
    { value: CYCLE_TYPE_IRREGULAR, label_translation: 'irregular_cycle_type' },
    { value: CYCLE_TYPE_PREGNANT, label_translation: 'pregnant' }
];

export const contraceptionTypes = [
    { value: CONTRACEPTION_TYPE_CONTRACEPTIVE_INJECTIONS, label_translation: 'label_contraceptive_injection' },
    { value: CONTRACEPTION_TYPE_CONTRACEPTIVE_PATCH, label_translation: 'label_contraceptive_patch' },
    { value: CONTRACEPTION_TYPE_IUD, label_translation: 'label_IUD' },
    { value: CONTRACEPTION_TYPE_IMPLANT, label_translation: 'label_implant' },
    { value: CONTRACEPTION_TYPE_ORAL_CONTRACEPTION, label_translation: 'label_oral_contraceptive' },
    { value: CONTRACEPTION_TYPE_VAGINAL_RING, label_translation: 'label_vaginal_ring' },
    { value: CONTRACEPTION_TYPE_NONE, label_translation: 'label_none' }
];

/**
 * Sorts the symptoms by it's translated value
 *
 * @param {*} symptoms
 */
export const sortPhysicalSymptoms = (symptoms) => {
    let sortSym = symptoms.map((symptomValue) => {
        return {
            key: symptomValue,
            label: i18next.t(`menstrual-cycle-tracking:${symptomValue.toLowerCase()}`)
        };
    });

    sortSym.sort((a, b) => {
        return a.label.localeCompare(b.label);
    });

    symptoms = sortSym.map((s) => {
        return s.key;
    });

    return symptoms;
};

export const pushAnalyticsEvent = (action) => {
    AnalyticsUtil.push({
        event: 'track_event',
        category: "Women's Health Event",
        action: action
    });
};

export const calculatePeriodPhases = (
    fertileWindowStart,
    lengthOfFertileWindow,
    periodLength,
    predictedCycleLength = 28,
    ovulationPrediction,
    contraception,
    lutealPhaseStart
) => {
    const startMenstrualPhase = 1;
    const endMenstrualPhase = periodLength;
    let startFertilePhase = fertileWindowStart;
    const endFertilePhase = fertileWindowStart + lengthOfFertileWindow - 1;

    //if user has fertile predictions off startFertilePhase should be undefined
    if (startFertilePhase <= 0 && !isNaN(startFertilePhase)) {
        startFertilePhase = periodLength;
    }
    //Fertile Window Predictions ON
    if (ovulationPrediction && predictedCycleLength >= 20) {
        let startFollicularPhase = periodLength + 1;
        let endFollicularPhase = fertileWindowStart - 1;
        const startLutealPhase = endFertilePhase + 1;
        const endLutealPhase = predictedCycleLength;

        if (fertileWindowStart <= 0 && lengthOfFertileWindow > 0) {
            //if fertileWindow is negative - it comes right after period and follicular phase should be missing
            startFollicularPhase = null;
            endFollicularPhase = null;
        }

        return {
            startMenstrualPhase: startMenstrualPhase,
            endMenstrualPhase: endMenstrualPhase,
            startFollicularPhase: startFollicularPhase,
            endFollicularPhase: endFollicularPhase,
            startFertilePhase: startFertilePhase,
            endFertilePhase: endFertilePhase,
            startLutealPhase: startLutealPhase,
            endLutealPhase: endLutealPhase
        };
    } else {
        //Fertile Window Predictions OFF
        const startCyclePhase = periodLength;
        const endCyclePhase = predictedCycleLength ? predictedCycleLength : 28;

        const fertilePhaseEnd = lutealPhaseStart
            ? lutealPhaseStart
            : endFertilePhase
            ? endFertilePhase
            : endMenstrualPhase + 7;

        if (contraception === 'NONE' && predictedCycleLength >= 20) {
            return {
                startMenstrualPhase: startMenstrualPhase,
                endMenstrualPhase: endMenstrualPhase,
                endFertilePhase: fertilePhaseEnd,
                endCyclePhase: endCyclePhase
            };
        }

        return {
            startMenstrualPhase: startMenstrualPhase,
            endMenstrualPhase: endMenstrualPhase,
            startCyclePhase: startCyclePhase,
            endCyclePhase: endCyclePhase
        };
    }
};

export const getNoPeriodDayLogged = (predictedCycle, currentPhase) => {
    return noPeriodDayLogged({ predictedCycle, currentPhase });
};

export const getIsPeriodDayLogged = (predictedCycle, currentPhase) => {
    return isPeriodDayLogged({ predictedCycle, currentPhase });
};

export const getPredictionTitleAndSubtitle = (chartDetails) => {
    const {
        dayInCycle,
        calendarDate,
        selectedDate,
        isAPredictionSelected,
        userIsInactive,
        predictedCycle,
        currentPhase
    } = chartDetails;
    let phasesValues = {
        predictionsOn: true
    };
    const isSameDate = calendarDate && selectedDate ? selectedDate === calendarDate : false;

    if (isSameDate && !isAPredictionSelected && getIsPeriodDayLogged(predictedCycle, currentPhase)) {
        //all predictions off - period day logged
        phasesValues = {
            title: dayInCycle,
            subtitle: 'label_period_day',
            predictionsOn: false
        };
    } else if (!isAPredictionSelected || getNoPeriodDayLogged(predictedCycle, currentPhase)) {
        //all predictions off - no period day logged || all predictions off
        phasesValues = {
            title: dayInCycle === null ? '_ _' : dayInCycle,
            subtitle: 'label_cycle_day',
            predictionsOn: isAPredictionSelected
        };
    } else if (userIsInactive) {
        //inactive for 6 cycles
        //cycle type is none
        phasesValues = {
            title: '_ _',
            subtitle: 'label_cycle_day',
            predictionsOn: false
        };
    }
    return phasesValues;
};

export const getGaugePlotBands = (phasesValues, predictedCycleLength, chartDetails) => {
    const {
        periodLength,
        contraception,
        ovulationPrediction,
        currentPhase,
        fertileWindowStart,
        lengthOfFertileWindow,
        lutealPhaseStart
    } = chartDetails;
    //if phases.predictionsOn === false there is just one phase and is no need to calculatePeriodPhases
    if (phasesValues.predictionsOn) {
        const phases = calculatePeriodPhases(
            fertileWindowStart,
            lengthOfFertileWindow,
            periodLength,
            predictedCycleLength,
            ovulationPrediction,
            contraception,
            lutealPhaseStart
        );
        // If period length is 1, the berry segment is not visible if starts from 1 and ends to 1, only if ends with 2
        const plotBand = {
            color: chartBerry,
            newColor: ColorVioletPrimary,
            from: phases.startMenstrualPhase,
            to: periodLength === 1 ? 2 : phases.endMenstrualPhase,
            innerRadius: '92%',
            outerRadius: '108%',
            borderColor: chartWhite,
            borderWidth: 4
        };
        if (predictedCycleLength < 20) {
            return [
                {
                    ...plotBand,
                    color: chartGray,
                    newColor: ColorDarkuiAccent3,
                    from: phases.endMenstrualPhase,
                    to: predictedCycleLength
                },
                { ...plotBand }
            ];
        }
        //regular
        //all predictions
        //there is a case when period length is 1 and follicular length is 1
        let isOneFollicularDay =
            periodLength === 1 &&
            phases.endFollicularPhase !== null &&
            phases.startFollicularPhase !== null &&
            phases.endFollicularPhase - phases.startFollicularPhase === 0;

        let plotBands = [
            { ...plotBand },
            {
                ...plotBand,
                color: chartGray,
                newColor: ColorDarkuiAccent3,
                from: isOneFollicularDay ? 2 : phases.startFollicularPhase - 1,
                to: isOneFollicularDay ? 3 : phases.endFollicularPhase
            },
            {
                ...plotBand,
                color: contraception === 'NONE' ? chartGreen : chartGray,
                newColor: contraception === 'NONE' ? ColorGreenPrimary : ColorDarkuiAccent3,
                from: isOneFollicularDay ? 3 : phases.startFertilePhase - 1,
                to: phases.endFertilePhase
            },
            {
                ...plotBand,
                color: chartGray,
                newColor: ColorDarkuiAccent3,
                from: phases.startLutealPhase - 1,
                to: phases.endLutealPhase
            }
        ];
        if (!ovulationPrediction && contraception !== 'NONE') {
            //fertile window off
            //using contraception
            plotBands = [
                { ...plotBand },
                {
                    ...plotBand,
                    color: chartGray,
                    newColor: ColorDarkuiAccent3,
                    from: phases.startCyclePhase - 1,
                    to: phases.endCyclePhase
                }
            ];
        } else if (!ovulationPrediction && contraception === 'NONE') {
            //fertile window off
            //not using contraception
            plotBands = [
                { ...plotBand },
                {
                    ...plotBand,
                    color: chartGray,
                    newColor: ColorDarkuiAccent3,
                    from: phases.endMenstrualPhase,
                    to: phases.endFertilePhase
                },
                {
                    ...plotBand,
                    color: chartGray,
                    newColor: ColorDarkuiAccent3,
                    from: phases.endFertilePhase,
                    to: phases.endCyclePhase
                }
            ];
        }
        return plotBands;
    } else {
        //all predictions off
        //inactive for 6 cycles
        // cycle type none
        return [
            {
                color: currentPhase === 1 ? chartBerry : chartGray,
                newColor: currentPhase === 1 ? ColorVioletPrimary : ColorDarkuiAccent3,
                from: 1,
                to: predictedCycleLength ? predictedCycleLength : 28,
                innerRadius: '92%',
                outerRadius: '108%',
                borderColor: chartWhite,
                borderWidth: 4
            }
        ];
    }
};

export const getGaugeChartSlicesInDays = (phasesValues, predictedCycleLength, chartDetails) => {
    const gaugePlotBands = getGaugePlotBands(phasesValues, predictedCycleLength, chartDetails);
    const { currentPhase } = chartDetails;

    return gaugePlotBands.map((band, index) => {
        const isOnCurrentPhase = currentPhase && currentPhase === index + 1;
        const from = band.from;
        const to = band.to;
        const value = isOnCurrentPhase ? to - from + 1 : to - from;

        return {
            color: band.newColor,
            value: value,
            secondaryMetricText: ''
        };
    });
};
